import React, { useEffect, useState } from 'react';
import { useArticles } from '../../contexts/ArticlesContext';
import { useSources } from '../../contexts/SourcesContext';
import { useAuth } from '../../hooks/useAuth';
import apiService from '../../services/api';
import { ModelComparisonModal } from '../ModelComparisonModal';
import { ArticleDetailModal } from './articles/ArticleDetailModal';
import { ArticlesFilters } from './articles/ArticlesFilters';
import { ArticlesTable } from './articles/ArticlesTable';
import { BulkActions } from './articles/BulkActions';

const ARTICLE_REFRESH_INTERVAL = 10000; // 10 seconds

export function ArticlesTab() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { sources } = useSources();
  const {
    articles,
    loading,
    error,
    selectedSourceId,
    viewingArticle,
    pagination,
    setSelectedSourceId,
    setViewingArticle,
    fetchArticles,
    triggerAnalysis,
    setPage,
    setPageSize,
    goToFirstPage,
    goToLastPage,
    goToNextPage,
    goToPrevPage
  } = useArticles();

  // Bulk selection state
  const [selectedArticleIds, setSelectedArticleIds] = React.useState<Set<number>>(new Set());
  const [bulkAnalyzing, setBulkAnalyzing] = React.useState(false);
  const [useRelevanceCheck, setUseRelevanceCheck] = React.useState(false);
  
  // Track last analyzed article for highlighting
  const [lastAnalyzedArticleId, setLastAnalyzedArticleId] = React.useState<number | null>(null);
  
  // Model comparison modal state
  const [comparingArticle, setComparingArticle] = React.useState<any>(null);
  
  // Filter state
  const [filters, setFilters] = React.useState({
    search: '',
    analysisStatus: 'all' as 'all' | 'analyzed' | 'pending' | 'unanalyzed' | 'not_relevant',
    minRiskScore: 0,
    threatCategory: '',
    sortBy: 'date' as 'date' | 'risk' | 'title',
    sortOrder: 'desc' as 'asc' | 'desc'
  });
  
  // Categories for filter
  interface ThreatCategory {
    id: number;
    code: string;
    name: string;
    isActive: boolean;
    deletedAt?: string | null;
  }
  const [categories, setCategories] = useState<ThreatCategory[]>([]);

  // Fetch articles when component mounts or dependencies change
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchArticles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, authLoading, selectedSourceId, pagination.currentPage, pagination.pageSize]);

  // Fetch categories for filter
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      apiService.getCategories().then(cats => {
        setCategories(cats.filter(c => c.isActive && !c.deletedAt));
      }).catch(console.error);
    }
  }, [isAuthenticated, authLoading]);

  // Auto-refresh articles when there might be processing happening
  useEffect(() => {
    if (!isAuthenticated || authLoading) return;

    const hasProcessingArticles = articles.some(article => article.hasPendingJob);
    if (!hasProcessingArticles) return;

    const interval = setInterval(() => {
      fetchArticles();
    }, ARTICLE_REFRESH_INTERVAL);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articles, isAuthenticated, authLoading]);

  // Scroll to last analyzed article when it appears in the list
  useEffect(() => {
    if (lastAnalyzedArticleId && articles.length > 0) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        const element = document.querySelector(`tr[data-article-id="${lastAnalyzedArticleId}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      
      // Clear the highlight after 5 seconds
      const clearTimer = setTimeout(() => {
        setLastAnalyzedArticleId(null);
      }, 5000);
      
      return () => clearTimeout(clearTimer);
    }
  }, [lastAnalyzedArticleId, articles]);

  // Analysis operation state
  const [analyzingArticleId, setAnalyzingArticleId] = React.useState<number | null>(null);
  const [deletingAnalysisId, setDeletingAnalysisId] = React.useState<number | null>(null);
  const [analysisResult, setAnalysisResult] = React.useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const handleAnalyzeArticle = async (articleId: number) => {
    try {
      setAnalyzingArticleId(articleId);
      setAnalysisResult(null);
      setLastAnalyzedArticleId(articleId);
      
      // Store current selection before analysis
      const currentSelection = new Set(selectedArticleIds);
      
      await triggerAnalysis(articleId, false); // Individual analysis goes directly to tier 2
      
      // Restore selection after analysis
      setSelectedArticleIds(currentSelection);
      
      // Force refresh to update extraction status
      fetchArticles();
      
      setAnalysisResult({
        type: 'success',
        message: 'AI analysis triggered successfully'
      });
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setAnalysisResult(null);
      }, 3000);
    } catch (error) {
      console.error('Failed to trigger article analysis:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to trigger analysis';
      const axiosError = error as { response?: { data?: { message?: string } } };
      const finalMessage = axiosError.response?.data?.message || errorMessage;
      setAnalysisResult({
        type: 'error',
        message: finalMessage
      });
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setAnalysisResult(null);
      }, 5000);
    } finally {
      setAnalyzingArticleId(null);
    }
  };

  const handleDeleteAnalysis = async (articleId: number) => {
    if (!window.confirm('Are you sure you want to delete this analysis? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingAnalysisId(articleId);
      setAnalysisResult(null);
      
      await apiService.deleteArticleAnalysis(articleId, 'EST');
      
      setAnalysisResult({
        type: 'success',
        message: 'Analysis deleted successfully'
      });
      
      // Refresh articles to show updated state
      fetchArticles();
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setAnalysisResult(null);
      }, 3000);
    } catch (error) {
      console.error('Failed to delete analysis:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete analysis';
      const axiosError = error as { response?: { data?: { message?: string } } };
      const finalMessage = axiosError.response?.data?.message || errorMessage;
      setAnalysisResult({
        type: 'error',
        message: finalMessage
      });
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setAnalysisResult(null);
      }, 5000);
    } finally {
      setDeletingAnalysisId(null);
    }
  };

  const handleBulkAnalyze = async () => {
    if (selectedArticleIds.size === 0) return;
    
    setBulkAnalyzing(true);
    setAnalysisResult(null);
    
    let successCount = 0;
    let errorCount = 0;
    
    try {
      // Process articles in batches to avoid overwhelming the system
      const articleIds = Array.from(selectedArticleIds);
      
      for (const articleId of articleIds) {
        try {
          await triggerAnalysis(articleId, useRelevanceCheck);
          successCount++;
        } catch (error) {
          console.error(`Failed to analyze article ${articleId}:`, error);
          errorCount++;
        }
      }
      
      // Don't clear selection here, let user manually clear if needed
      // This maintains selection after bulk operations
      
      setAnalysisResult({
        type: errorCount === 0 ? 'success' : 'error',
        message: `Analysis triggered for ${successCount} articles${errorCount > 0 ? `, ${errorCount} failed` : ''}${useRelevanceCheck ? ' (using tier 1 filter)' : ' (direct to tier 2)'}`
      });
      
      // Clear message after 5 seconds
      setTimeout(() => {
        setAnalysisResult(null);
      }, 5000);
    } finally {
      setBulkAnalyzing(false);
    }
  };

  const handleBulkDeleteAnalysis = async () => {
    if (selectedArticleIds.size === 0) return;
    
    // Filter to only articles that have analysis or relevance check
    const articlesWithAnalysis = filteredArticles.filter(article => 
      selectedArticleIds.has(article.id) && (article.hasAnalysis || article.relevanceChecked)
    );
    
    if (articlesWithAnalysis.length === 0) {
      setAnalysisResult({
        type: 'error',
        message: 'No selected articles have analysis or relevance check to delete'
      });
      setTimeout(() => setAnalysisResult(null), 3000);
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete analysis for ${articlesWithAnalysis.length} articles? This action cannot be undone.`)) {
      return;
    }
    
    setBulkAnalyzing(true);
    setAnalysisResult(null);
    
    let successCount = 0;
    let errorCount = 0;
    
    try {
      for (const article of articlesWithAnalysis) {
        try {
          await apiService.deleteArticleAnalysis(article.id, 'EST');
          successCount++;
        } catch (error) {
          console.error(`Failed to delete analysis for article ${article.id}:`, error);
          errorCount++;
        }
      }
      
      setAnalysisResult({
        type: errorCount === 0 ? 'success' : 'error',
        message: `Analysis deleted for ${successCount} articles${errorCount > 0 ? `, ${errorCount} failed` : ''}`
      });
      
      // Don't clear selection automatically
      fetchArticles(); // Refresh to show updated state
      
      // Clear message after 5 seconds
      setTimeout(() => {
        setAnalysisResult(null);
      }, 5000);
    } finally {
      setBulkAnalyzing(false);
    }
  };

  const handleAnalyzeAllNonAnalyzed = async () => {
    if (!window.confirm('This will analyze ALL non-analyzed articles in the database (not just those displayed) including relevance check. Continue?')) {
      return;
    }
    
    setBulkAnalyzing(true);
    setAnalysisResult(null);
    
    try {
      const result = await apiService.triggerBulkAnalysis({
        countryCode: 'EST',
        useRelevanceCheck: true,
      });
      
      setAnalysisResult({
        type: 'success',
        message: result.message
      });
      
      // Refresh articles to show updated processing status
      fetchArticles();
      
      // Clear message after 5 seconds
      setTimeout(() => {
        setAnalysisResult(null);
      }, 5000);
    } catch (error) {
      console.error('Failed to trigger bulk analysis:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to trigger bulk analysis';
      const axiosError = error as { response?: { data?: { message?: string } } };
      const finalMessage = axiosError.response?.data?.message || errorMessage;
      
      setAnalysisResult({
        type: 'error',
        message: finalMessage
      });
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setAnalysisResult(null);
      }, 5000);
    } finally {
      setBulkAnalyzing(false);
    }
  };

  const handleDeleteAllArticles = async () => {
    if (!window.confirm('⚠️ This will PERMANENTLY DELETE ALL articles in the database. This action cannot be undone. Continue?')) {
      return;
    }
    
    // Double confirmation for such a destructive action
    if (!window.confirm('Are you absolutely sure? This will delete ALL articles and their analyses from the database.')) {
      return;
    }
    
    setBulkAnalyzing(true);
    setAnalysisResult(null);
    
    try {
      const result = await apiService.deleteAllArticles();
      
      setAnalysisResult({
        type: 'success',
        message: result.message
      });
      
      // Refresh articles to show updated state
      fetchArticles();
      
      // Clear message after 5 seconds
      setTimeout(() => {
        setAnalysisResult(null);
      }, 5000);
    } catch (error) {
      console.error('Failed to delete articles:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete articles';
      const axiosError = error as { response?: { data?: { message?: string } } };
      const finalMessage = axiosError.response?.data?.message || errorMessage;
      
      setAnalysisResult({
        type: 'error',
        message: finalMessage
      });
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setAnalysisResult(null);
      }, 5000);
    } finally {
      setBulkAnalyzing(false);
    }
  };

  const handleClearAllAnalysisData = async () => {
    if (!window.confirm('⚠️ This will CLEAR ALL analysis and relevance data from ALL articles (but keep the articles). Continue?')) {
      return;
    }
    
    setBulkAnalyzing(true);
    setAnalysisResult(null);
    
    try {
      const result = await apiService.clearAllAnalysisData();
      
      setAnalysisResult({
        type: 'success',
        message: result.message
      });
      
      // Refresh articles to show updated state
      fetchArticles();
      
      // Clear message after 5 seconds
      setTimeout(() => {
        setAnalysisResult(null);
      }, 5000);
    } catch (error) {
      console.error('Failed to clear analysis data:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to clear analysis data';
      const axiosError = error as { response?: { data?: { message?: string } } };
      const finalMessage = axiosError.response?.data?.message || errorMessage;
      
      setAnalysisResult({
        type: 'error',
        message: finalMessage
      });
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setAnalysisResult(null);
      }, 5000);
    } finally {
      setBulkAnalyzing(false);
    }
  };

  // Filter and sort articles
  const filteredArticles = React.useMemo(() => {
    let filtered = [...articles];
    
    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(article => 
        article.title.toLowerCase().includes(searchLower) ||
        article.content?.toLowerCase().includes(searchLower) ||
        article.url.toLowerCase().includes(searchLower)
      );
    }
    
    // Analysis status filter
    if (filters.analysisStatus !== 'all') {
      filtered = filtered.filter(article => {
        if (filters.analysisStatus === 'analyzed') return article.hasAnalysis;
        if (filters.analysisStatus === 'pending') return article.hasPendingJob;
        if (filters.analysisStatus === 'unanalyzed') return !article.hasAnalysis && !article.hasPendingJob && !(article.relevanceChecked && !article.isRelevant);
        if (filters.analysisStatus === 'not_relevant') return article.relevanceChecked && !article.isRelevant;
        return true;
      });
    }
    
    // Risk score filter
    if (filters.minRiskScore > 0) {
      filtered = filtered.filter(article => 
        article.latestAnalysis && article.latestAnalysis.overallRiskScore >= filters.minRiskScore
      );
    }
    
    // Threat category filter
    if (filters.threatCategory) {
      filtered = filtered.filter(article => {
        if (!article.latestAnalysis?.categoryScores) return false;
        const categoryScore = article.latestAnalysis.categoryScores[filters.threatCategory];
        return categoryScore && categoryScore >= 1;
      });
    }
    
    // Sorting with stable secondary sort by ID to maintain consistent order
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (filters.sortBy) {
        case 'date':
          comparison = new Date(b.publishedAt || b.scrapedAt).getTime() - 
                      new Date(a.publishedAt || a.scrapedAt).getTime();
          break;
        case 'risk':
          const riskA = a.latestAnalysis?.overallRiskScore || 0;
          const riskB = b.latestAnalysis?.overallRiskScore || 0;
          comparison = riskB - riskA;
          break;
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
      }
      
      // If primary sort values are equal, use ID as secondary sort for stability
      if (comparison === 0) {
        comparison = b.id - a.id; // Newer articles (higher IDs) first
      }
      
      return filters.sortOrder === 'asc' ? -comparison : comparison;
    });
    
    return filtered;
  }, [articles, filters]);

  // Helper functions
  const isAllSelected = filteredArticles.length > 0 && 
    filteredArticles.every(article => selectedArticleIds.has(article.id));
  
  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedArticleIds(new Set());
    } else {
      setSelectedArticleIds(new Set(filteredArticles.map(a => a.id)));
    }
  };
  
  const handleSelectArticle = (articleId: number) => {
    const newSelected = new Set(selectedArticleIds);
    if (newSelected.has(articleId)) {
      newSelected.delete(articleId);
    } else {
      newSelected.add(articleId);
    }
    setSelectedArticleIds(newSelected);
  };

  const handleSort = (column: 'date' | 'risk' | 'title') => {
    if (filters.sortBy === column) {
      // Toggle sort order if same column
      setFilters({ ...filters, sortOrder: filters.sortOrder === 'asc' ? 'desc' : 'asc' });
    } else {
      // Set new column with default order
      setFilters({ ...filters, sortBy: column, sortOrder: column === 'date' || column === 'risk' ? 'desc' : 'asc' });
    }
  };

  const getSortIcon = (column: 'date' | 'risk' | 'title') => {
    if (filters.sortBy !== column) return '↕️';
    return filters.sortOrder === 'asc' ? '↑' : '↓';
  };

  if (error) {
    return (
      <div className="tc-card p-6">
        <div className="text-center text-red-600">
          <p>Error: {error}</p>
          <button onClick={fetchArticles} className="tc-btn tc-btn-primary mt-4">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Articles List */}
      <div className="tc-card p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold tc-text-primary">Scraped Articles</h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleAnalyzeAllNonAnalyzed}
              disabled={bulkAnalyzing || loading}
              className="tc-btn tc-btn-primary"
            >
              {bulkAnalyzing ? '⏳ Processing...' : '🤖 Analyze All Non-Analyzed'}
            </button>
            <button
              onClick={handleClearAllAnalysisData}
              disabled={bulkAnalyzing || loading}
              className="tc-btn tc-btn-secondary"
            >
              {bulkAnalyzing ? '⏳ Processing...' : '🧹 Clear All Analysis Data'}
            </button>
            <button
              onClick={handleDeleteAllArticles}
              disabled={bulkAnalyzing || loading}
              className="tc-btn tc-btn-danger"
            >
              {bulkAnalyzing ? '⏳ Processing...' : '🗑️ Delete All Articles'}
            </button>
            <button
              onClick={fetchArticles}
              disabled={loading}
              className="tc-btn tc-btn-primary"
            >
              {loading ? '🔄 Loading...' : '🔄 Refresh'}
            </button>
          </div>
        </div>

        {/* Filter Controls */}
        <ArticlesFilters
          filters={filters}
          setFilters={setFilters}
          sources={sources}
          categories={categories}
          selectedSourceId={selectedSourceId}
          setSelectedSourceId={setSelectedSourceId}
        />

        {/* Bulk Actions */}
        <BulkActions
          selectedArticleIds={selectedArticleIds}
          bulkAnalyzing={bulkAnalyzing}
          useRelevanceCheck={useRelevanceCheck}
          setUseRelevanceCheck={setUseRelevanceCheck}
          filteredArticles={filteredArticles}
          onBulkAnalyze={handleBulkAnalyze}
          onBulkDeleteAnalysis={handleBulkDeleteAnalysis}
          onClearSelection={() => setSelectedArticleIds(new Set())}
        />

        {/* Analysis Status Message */}
        {analysisResult && (
          <div className={`p-4 rounded-md mb-4 ${
            analysisResult.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            <div className="flex items-center">
              <span className="mr-2">
                {analysisResult.type === 'success' ? '✅' : '❌'}
              </span>
              {analysisResult.message}
            </div>
          </div>
        )}

        {/* Results Summary */}
        <div className="mb-4 text-sm tc-text-secondary">
          Showing {filteredArticles.length} of {articles.length} articles
          {filters.search && ` matching "${filters.search}"`}
          {filters.analysisStatus === 'not_relevant' && ` that are not relevant (filtered by AI)`}
          {filters.minRiskScore > 0 && ` with risk ≥ ${filters.minRiskScore}`}
          {filters.threatCategory && ` in ${categories.find(c => c.code === filters.threatCategory)?.name || filters.threatCategory}`}
        </div>

        {/* Articles Table */}
        <ArticlesTable
          articles={articles}
          filteredArticles={filteredArticles}
          filters={filters}
          selectedArticleIds={selectedArticleIds}
          selectedSourceId={selectedSourceId}
          lastAnalyzedArticleId={lastAnalyzedArticleId}
          analyzingArticleId={analyzingArticleId}
          deletingAnalysisId={deletingAnalysisId}
          loading={loading}
          sources={sources}
          onSort={handleSort}
          onSelectAll={handleSelectAll}
          onSelectArticle={handleSelectArticle}
          onViewArticle={setViewingArticle}
          onAnalyzeArticle={handleAnalyzeArticle}
          onDeleteAnalysis={handleDeleteAnalysis}
          onCompareArticle={setComparingArticle}
          getSortIcon={getSortIcon}
        />

        {/* Pagination Controls */}
        <div className="mt-6 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <label className="text-sm tc-text-secondary">Show:</label>
            <select
              value={pagination.pageSize}
              onChange={(e) => setPageSize(parseInt(e.target.value))}
              className="tc-input text-sm"
            >
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            <span className="text-sm tc-text-secondary">per page</span>
          </div>

          {/* Pagination Navigation */}
          {pagination.totalPages > 1 && (
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <button
                  onClick={goToFirstPage}
                  disabled={!pagination.hasPrevPage || loading}
                  className="tc-btn tc-btn-secondary text-sm disabled:opacity-50"
                >
                  ⏮️ First
                </button>
                <button
                  onClick={goToPrevPage}
                  disabled={!pagination.hasPrevPage || loading}
                  className="tc-btn tc-btn-secondary text-sm disabled:opacity-50"
                >
                  ⬅️ Prev
                </button>
              </div>

              <div className="flex items-center space-x-1">
                <span className="text-sm tc-text-secondary">Page</span>
                <input
                  type="number"
                  min="1"
                  max={pagination.totalPages}
                  value={pagination.currentPage}
                  onChange={(e) => {
                    const page = parseInt(e.target.value);
                    if (page >= 1 && page <= pagination.totalPages) {
                      setPage(page);
                    }
                  }}
                  className="tc-input w-16 text-sm text-center"
                />
                <span className="text-sm tc-text-secondary">of {pagination.totalPages}</span>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={goToNextPage}
                  disabled={!pagination.hasNextPage || loading}
                  className="tc-btn tc-btn-secondary text-sm disabled:opacity-50"
                >
                  Next ➡️
                </button>
                <button
                  onClick={goToLastPage}
                  disabled={!pagination.hasNextPage || loading}
                  className="tc-btn tc-btn-secondary text-sm disabled:opacity-50"
                >
                  Last ⏭️
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Article Details Modal */}
      {viewingArticle && (
        <ArticleDetailModal
          article={viewingArticle}
          onClose={() => setViewingArticle(null)}
        />
      )}

      {/* Model Comparison Modal */}
      {comparingArticle && (
        <ModelComparisonModal
          article={comparingArticle}
          onClose={() => setComparingArticle(null)}
        />
      )}
    </div>
  );
}